import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  CheckCircle2, 
  X, 
  Crown, 
  Building2, 
  Users, 
  TrendingUp, 
  Target, 
  CreditCard,
  Home,
  FileText,
  Sparkles,
  Shield,
  Clock,
  Loader2,
  ArrowRight,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import PayPalButton from "../components/PayPalButton";
import ClosingCostEstimator from "../components/ClosingCostEstimator";
import { useAuth } from "../context/AuthContext";
import ReviewSubmitModal from "../components/ReviewSubmitModal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FreeResultsPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [results, setResults] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [upgraded, setUpgraded] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  // True when the applicant already paid for a Pro report via the
  // Buying Power calculator. We skip the paywall and route them to
  // the sign-in / create-account screen to view their premium results.
  const [bpAlreadyPaid, setBpAlreadyPaid] = useState(false);

  useEffect(() => {
    // Get results from session storage
    const storedResults = sessionStorage.getItem("qualificationResult");
    if (storedResults) {
      const parsed = JSON.parse(storedResults);
      setResults(parsed);
      // Check if already pro — set by backend auto-upgrade when the
      // applicant's email matches a paid Buying Power lead.
      if (parsed.is_pro) {
        setUpgraded(true);
        setBpAlreadyPaid(true);
      }
    } else {
      toast.error("No results found. Please complete the qualification form.");
      navigate("/qualify");
    }
  }, [navigate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const freeFeatures = [
    { text: "Your TDSR Score", included: true },
    { text: "Maximum Qualification Amount", included: true },
    { text: "Estimated Monthly Payment", included: true },
    { text: "See Which Banks You Qualify With", included: false },
    { text: "Direct Connection to Loan Officer", included: false },
    { text: "Personal Financial Planner", included: false },
    { text: "TDSR Improvement Roadmap", included: false },
    { text: "Debt Reduction Strategy", included: false },
    { text: "Home Buying Guidance", included: false },
  ];

  const proFeatures = [
    { text: "Your TDSR Score", included: true },
    { text: "Maximum Qualification Amount", included: true },
    { text: "Estimated Monthly Payment", included: true },
    { text: "See Which Banks You Qualify With", included: true, highlight: true },
    { text: "Direct Connection to Loan Officer", included: true, highlight: true },
    { text: "Personal Financial Planner", included: true, highlight: true },
    { text: "TDSR Improvement Roadmap", included: true, highlight: true },
    { text: "Debt Reduction Strategy", included: true, highlight: true },
    { text: "Home Buying Guidance", included: true, highlight: true },
  ];

  const financialPlannerServices = [
    { icon: TrendingUp, title: "TDSR Improvement", desc: "Personalized plan to lower your debt ratio" },
    { icon: CreditCard, title: "Debt Reduction", desc: "Principal payment strategies & credit card advice" },
    { icon: Target, title: "Early Repayment", desc: "Roadmap to pay off loans faster" },
    { icon: Home, title: "Home Buying Guide", desc: "Requirements & steps to homeownership" },
    { icon: FileText, title: "Comprehensive Report", desc: "Detailed financial health analysis" },
  ];

  const handlePaymentSuccess = async (paymentData) => {
    setProcessing(true);
    try {
      // Call upgrade endpoint
      const response = await axios.post(`${API}/api/upgrade-to-pro`, {
        application_id: results.id,
        transaction_id: paymentData.transaction_id
      }, {
        headers: getAuthHeader()
      });
      
      // Update local results
      const updatedResults = { ...results, is_pro: true };
      setResults(updatedResults);
      sessionStorage.setItem("qualificationResult", JSON.stringify(updatedResults));
      
      setUpgraded(true);
      toast.success("Welcome to Pro! You now have full access to your results.");
      
      // Navigate to full results page
      setTimeout(() => {
        navigate("/results");
      }, 1500);
      
    } catch (error) {
      console.error("Upgrade error:", error);
      toast.error("Failed to process upgrade. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleContinueFree = () => {
    // Navigate to dashboard with limited view
    navigate("/dashboard");
  };

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
      </div>
    );
  }

  // Already paid via Buying Power → skip the paywall entirely and send the
  // user to a sign-in / create-account screen so they can access their
  // premium results.
  if (bpAlreadyPaid) {
    const appId = results.id;
    const applicantEmail = results.applicant_email || "";
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-br from-[#f8fffe] via-white to-[#f0fafa]">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-100 p-8" data-testid="bp-paid-signup-prompt">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <div className="inline-flex items-center gap-1 bg-[#52BFB8]/10 text-[#3E9690] px-3 py-1 rounded-full text-xs font-semibold mb-3">
              <Crown className="w-3.5 h-3.5" /> Premium Already Unlocked
            </div>
            <h1 className="text-2xl font-bold text-[#2D3748] mb-2 font-cabinet">
              Your pre-qualification is ready
            </h1>
            <p className="text-sm text-gray-500">
              You already paid for Pro access through the Buying Power calculator — no second charge.
              Create a quick account (or sign in) to view your full lender matches and personalized plan.
            </p>
          </div>

          {user ? (
            <Button
              className="w-full h-12 bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
              onClick={() => navigate("/results")}
              data-testid="bp-paid-view-results-btn"
            >
              View My Premium Results
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <div className="space-y-2">
              <Button
                className="w-full h-12 bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                onClick={() => navigate(`/signup?next=/results&app=${encodeURIComponent(appId || "")}&email=${encodeURIComponent(applicantEmail)}`)}
                data-testid="bp-paid-create-account-btn"
              >
                Create an Account to View Results
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => navigate(`/login?next=/results&app=${encodeURIComponent(appId || "")}&email=${encodeURIComponent(applicantEmail)}`)}
                data-testid="bp-paid-sign-in-btn"
              >
                I already have an account
              </Button>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            Having trouble?{" "}
            <a href="mailto:support@financify-caribbean.com" className="text-[#3E9690] hover:underline">
              support@financify-caribbean.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Calculate free results from matched_banks
  const maxAmount = results.matched_banks?.length > 0 
    ? Math.max(...results.matched_banks.map(b => b.max_approved_amount))
    : results.requested_amount;
  
  const avgPayment = results.matched_banks?.length > 0
    ? results.matched_banks.reduce((sum, b) => sum + b.estimated_monthly_payment, 0) / results.matched_banks.length
    : (() => {
        // Fallback: estimate from requested amount at 14% over 7 years
        const principal = results.requested_amount || 0;
        if (principal <= 0) return 0;
        const r = 0.14 / 12;
        const n = 7 * 12;
        return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      })();
  
  const bankCount = results.matched_banks?.length || 0;
  const displayBankCount = bankCount > 0 ? bankCount : (results.status === "qualified" ? 1 : 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Free Results Summary */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <CheckCircle2 className="w-4 h-4" />
            Pre-Qualification Complete!
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Your Free Results
          </h1>
          <p className="text-gray-600">Here's what we found based on your information</p>
        </div>

        {/* Free Results Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Your TDSR</p>
            <p className="text-3xl font-bold text-gray-900">{results.tdsr_percentage?.toFixed(1) || 0}%</p>
            <p className="text-xs text-gray-400 mt-1">
              {results.tdsr_percentage <= 40 ? "Good standing" : results.tdsr_percentage <= 50 ? "Moderate" : "High"}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-500 mb-1">You Could Qualify For</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(maxAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">Maximum loan amount</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Est. Monthly Payment</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(avgPayment)}</p>
            <p className="text-xs text-gray-400 mt-1">At current rates</p>
          </div>
        </div>

        {/* Closing Cost Estimator — mortgage flow only, free, no paywall */}
        {results.loan_type === "home" && (
          <div className="mb-10" data-testid="prequal-estimator-wrap">
            <ClosingCostEstimator
              mode="free"
              qualifiedLoan={maxAmount}
              initialInputs={{
                price: results.property_sale_price || (maxAmount ? Math.round(maxAmount / 0.9) : 350000),
                down_pct: 0.10,
                is_bahamian: results.citizenship_status === "citizen",
                first_time: false,
                age_band: (results.age || 30) >= 50 ? "over_50" : (results.age || 30) >= 40 ? "40_50" : "under_40",
              }}
              bankMatches={(results.matched_banks || []).map((b) => ({
                bank_id: b.bank_id,
                bank_name: b.bank_name,
                closing_fee_pct: b.closing_fee_pct,
                home_insurance_pct: b.home_insurance_pct,
                life_insurance_pct: b.life_insurance_pct,
              }))}
            />
          </div>
        )}

        {/* Banks Qualified Teaser */}
        <div className="bg-gradient-to-r from-[#52BFB8]/10 to-[#52BFB8]/5 rounded-2xl p-6 mb-8 border border-[#52BFB8]/20">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#52BFB8] rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {displayBankCount} Bank{displayBankCount !== 1 ? 's' : ''} Match Your Profile!
                </p>
                <p className="text-sm text-gray-600">Upgrade to see which banks and their offers</p>
              </div>
            </div>
            <div className="flex gap-2">
              {[1,2,3,4,5].slice(0, Math.min(bankCount, 5)).map((_, i) => (
                <div key={i} className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-gray-400" />
                </div>
              ))}
              {bankCount > 5 && (
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-medium text-gray-500">
                  +{bankCount - 5}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Review Request - Internal System */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 mb-10 border border-amber-200">
          <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0">
              <Star className="w-7 h-7 text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Help Other Bahamians Discover Financify</h3>
              <p className="text-sm text-gray-600">
                Your experience could help someone else get the loan clarity they need. 
                Share a quick review - it only takes a minute!
              </p>
            </div>
            <Button
              onClick={() => setShowReviewModal(true)}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 font-medium px-6 py-3 rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md flex-shrink-0"
              data-testid="leave-review-btn"
            >
              <Star className="w-5 h-5 text-amber-500" />
              Leave a Review
            </Button>
          </div>
        </div>

        {/* Pricing Comparison */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Unlock Your Full Results
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Get personalized bank matches and expert financial guidance
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-1">Free</h3>
                <p className="text-4xl font-bold text-gray-900">$0</p>
                <p className="text-sm text-gray-500">Basic insights</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                {freeFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    {feature.included ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                    <span className={feature.included ? "text-gray-700" : "text-gray-400"}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleContinueFree}
              >
                Continue with Free
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="bg-gradient-to-b from-[#52BFB8]/5 to-white rounded-2xl p-6 border-2 border-[#52BFB8] shadow-lg relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#52BFB8] text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <Crown className="w-4 h-4" /> Recommended
                </span>
              </div>
              
              <div className="text-center mb-6 pt-2">
                <h3 className="text-lg font-semibold text-[#52BFB8] mb-1">Pro</h3>
                <p className="text-4xl font-bold text-gray-900">$29<span className="text-xl">.99</span></p>
                <p className="text-sm text-gray-500">One-time payment</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${feature.highlight ? 'text-[#52BFB8]' : 'text-green-500'}`} />
                    <span className={`${feature.highlight ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                      {feature.text}
                    </span>
                    {feature.highlight && (
                      <span className="bg-[#52BFB8]/10 text-[#52BFB8] text-xs px-2 py-0.5 rounded-full">Pro</span>
                    )}
                  </li>
                ))}
              </ul>
              
              {!showPayment ? (
                <Button 
                  className="w-full bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                  onClick={() => setShowPayment(true)}
                  disabled={processing}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Pro - $29.99
                </Button>
              ) : (
                <div className="space-y-4">
                  {/* Promo Code */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Promo code"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      disabled={!!promoApplied}
                      data-testid="promo-code-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!promoCode.trim() || promoLoading || !!promoApplied}
                      onClick={async () => {
                        setPromoLoading(true);
                        try {
                          const res = await axios.post(`${API}/api/promo/validate`, { code: promoCode.trim() });
                          setPromoApplied(res.data);
                          toast.success(`Promo applied! ${res.data.discount_type === 'free' ? 'Free access!' : `${res.data.discount_value}% off`}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Invalid promo code");
                        }
                        setPromoLoading(false);
                      }}
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {promoApplied && (
                    <p className="text-xs text-[#52BFB8] font-medium">
                      {promoApplied.discount_type === 'free' ? 'Free access applied!' : `${promoApplied.discount_value}% discount applied`}
                    </p>
                  )}

                  {processing ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-[#52BFB8]" />
                      <span className="ml-2 text-gray-600">Processing upgrade...</span>
                    </div>
                  ) : (
                    <>
                      <PayPalButton
                        amount={29.99}
                        onSuccess={handlePaymentSuccess}
                        onError={(error) => {
                          console.error("Payment error:", error);
                          toast.error("Payment failed. Please try again.");
                        }}
                        disabled={processing}
                      />
                      <button 
                        onClick={() => setShowPayment(false)}
                        className="w-full text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Financial Planner Benefits */}
        <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#52BFB8] rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Pro Includes: Personal Financial Planner</h3>
              <p className="text-sm text-gray-600">Get expert guidance tailored to your goals</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {financialPlannerServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-4 text-center">
                  <div className="w-10 h-10 bg-[#52BFB8]/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Icon className="w-5 h-5 text-[#52BFB8]" />
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm mb-1">{service.title}</h4>
                  <p className="text-xs text-gray-500">{service.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Instant Access</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>No Hidden Fees</span>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <ReviewSubmitModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        authHeader={getAuthHeader()}
      />
    </div>
  );
}
